/**
 * @fileoverview ArcPageShell — the standard page frame for every Arc screen.
 *
 * WHEN TO USE: every Arc page, both sidebar destinations (Dashboard,
 * Projects, Students, Task Tracker, Reports, Resource Center, School
 * Settings) and subpages with a clear parent (Manage Faculty, School
 * Details, report subpages). Reference screen: /teacher/manage-students.
 *
 * PROPS
 * - `title`      required — rendered as `Title order={1}`.
 * - `subtitle`   optional — `Text color='dimmed' size='sm'` under the title.
 * - `actions`    optional — right-hand slot (buttons); stacks under the title
 *                 on mobile.
 * - `backTo` / `backLabel` optional pair — router-navigated back link above
 *                 the title (both or neither).
 *
 * THE RULER (spacing/width contract)
 * - No Container/max-width of its own: width comes from Layout's
 *   ContainerGlobal (max-width 120rem; side/bottom paddings 40/80/120
 *   desktop, 20/80/20 under 800px — fluid edge padding on small screens).
 * - Vertical rhythm (AR-578): fixed top bar (60px) -> ARC_PAGE_TOP_GAP
 *   (32px desktop, 24px <=800px — see ./constants) -> page title. The gap is
 *   the container's Arc top padding, so it is identical on every Arc screen
 *   and the title NEVER jumps when navigating.
 * - Header block is the FIRST element on the page, no extra top margin.
 * - Back link (when present): Anchor size sm + IconChevronLeft 14, absolutely
 *   positioned INSIDE the gap band (6px above the title row), so subpages
 *   with a back keep the title at the exact same position as every other
 *   screen.
 * - Title row: title+subtitle left, actions right, vertically centered;
 *   below the `sm` breakpoint (48em) it stacks column with `sm` gap and no
 *   horizontal overflow (actions wrap).
 * - Content: children render inside a `mt='md'` (24px) block; a child that
 *   brings its own top margin collapses with it, so existing screens keep
 *   their internal rhythm.
 *
 * BACK RULE: no back link on ANY screen that is a sidebar destination in the
 * Arc menu (Dashboard, Projects, My Tasks, Students, Task Tracker, Reports,
 * Resource Center, School Settings...), even when the screen is also
 * reachable from another page. Back only on subpages with a clear parent
 * that are NOT sidebar destinations (e.g. "Back to school settings" on
 * Manage Faculty / School Details; report subpages back to
 * /reports/dashboard). Back navigation always goes through the router
 * (persistent shell survives).
 *
 * MINIMAL USAGE
 *   <ArcPageShell
 *     title={t('screen.title')}
 *     subtitle={t('screen.subtitle')}
 *     actions={<Button onClick={...}>{t('screen.action')}</Button>}
 *   >
 *     ...page content, no Container/padding of its own...
 *   </ArcPageShell>
 */

import { Anchor, Box, Flex, Stack, Text, Title } from '@mantine/core'
import { IconChevronLeft } from '@tabler/icons-react'
import { Link } from 'react-router-dom'

const BACK_ICON_SIZE = 14
const TITLE_SUBTITLE_GAP = 4
/** Distance (px) between the back link and the title row below it. */
const BACK_LINK_GAP = 6

interface ArcPageShellProps {
  readonly title: React.ReactNode
  readonly subtitle?: React.ReactNode
  /** Right-hand header slot (buttons). Stacks below the title on mobile. */
  readonly actions?: React.ReactNode
  /** Router path of the parent screen. Only for subpages with a clear parent. */
  readonly backTo?: string
  readonly backLabel?: React.ReactNode
  readonly children: React.ReactNode
}

export const ArcPageShell = ({ title, subtitle, actions, backTo, backLabel, children }: ArcPageShellProps) => (
  <>
    <Box component='header' sx={{ position: 'relative' }}>
      {backTo && (
        <Anchor
          component={Link}
          to={backTo}
          size='sm'
          sx={{
            // Sits INSIDE the top-bar gap band so the title never moves on
            // screens that have a back link (see the ruler above).
            position: 'absolute',
            bottom: `calc(100% + ${BACK_LINK_GAP}px)`,
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          <IconChevronLeft size={BACK_ICON_SIZE} />
          {backLabel}
        </Anchor>
      )}
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        justify='space-between'
        align={{ base: 'flex-start', sm: 'center' }}
        gap='sm'
        wrap='wrap'
      >
        <Stack spacing={TITLE_SUBTITLE_GAP}>
          <Title order={1}>{title}</Title>
          {subtitle && (
            <Text color='dimmed' size='sm'>
              {subtitle}
            </Text>
          )}
        </Stack>
        {actions && (
          <Flex gap='sm' wrap='wrap'>
            {actions}
          </Flex>
        )}
      </Flex>
    </Box>
    <Box mt='md'>{children}</Box>
  </>
)
